import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Helmet } from 'react-helmet-async';
import { useMenu } from '../../hooks/useMenu';
import {
  createAccountType,
  patchAccountType,
} from '../../store/actions/application/applicationActions';
import useApplicationUid from '../../hooks/useApplicationUid';
import {
  ACCOUNT_TYPE_JOINT_TENANTS_COMMON,
  ACCOUNT_TYPE_JOINT_TENANTS_WITH_RIGHT_OF_SURVIVORSHIP,
} from '../../constants/accountTypes';
import useStepPost from '../../hooks/useStepPost';
import { useAccountData } from '../../hooks/useAccountData';
import useApplicationFundingData from '../../hooks/useApplicationFundingData';
import useBankApplicationFundingData from '../../hooks/useBankApplicationFundingData';
import {
  APPLICATION_FUNDING_TYPE,
  JointTenantsAccountType,
} from '../../util/constants';
import OnBoardingContainer from '../../components/OnBoarding/OnBoardingContainer';
import { Heading } from '../../components/Heading';
import FundingContainer from '../../components/FundingContainer';
import { parseEnumType } from '../../util/helpers/enumMappers';
import { selectSettings } from '../../store/selectors/settingsSelectors';
import useGtmStepHook from '../../util/hooks/useGtmStepHook';
import { retrieveFromSessionStorage } from '../../util/helpers/sessionStorageHelper';
import { ACCOUNT_TYPE_GTM } from '../../constants/sessionStorage';
import { gtmStepHelper } from '../../util/helpers/gtmHelper';
import { ACCOUNT_STEP_COMPLETE, ACCOUNT_STEP_VIEW } from '../../util/gtmEvents';
import { useApplicantData } from '../../hooks/useApplicantData';

const FundJointSurvivor = () => {
  const { t } = useTranslation();
  const { ApplicationUid } = useApplicationUid();
  const { activeMenu, activeItem } = useMenu();
  const AccountData = useAccountData();
  const { ApplicationFunding } = useApplicationFundingData();
  const { BankApplicationFunding } = useBankApplicationFundingData();
  const { BrokerName } = useSelector(selectSettings);
  const ApplicantData = useApplicantData();

  const { gtmStepEvent } = useGtmStepHook();
  const accountGtmType = retrieveFromSessionStorage(ACCOUNT_TYPE_GTM);

  useEffect(() => {
    gtmStepHelper({
      gtmStepEvent,
      eventName: ACCOUNT_STEP_VIEW,
      stepLabel: 'Account Survivor',
      accountType: accountGtmType,
      accountName: ApplicantData?.PersonName,
      accountDateOfBirth: ApplicantData?.PersonBirthday,
      accountPhone: ApplicantData?.Phone,
      accountAddress: ApplicantData?.PersonAddress,
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleGtmEvent = () => {
    gtmStepHelper({
      gtmStepEvent,
      eventName: ACCOUNT_STEP_COMPLETE,
      stepLabel: 'Account Survivor',
      accountType: accountGtmType,
      accountName: ApplicantData?.PersonName,
      accountDateOfBirth: ApplicantData?.PersonBirthday,
      accountPhone: ApplicantData?.Phone,
      accountAddress: ApplicantData?.PersonAddress,
    });
  };

  const dispatch = useDispatch();
  const { onSuccess } = useStepPost({ nextPath: activeItem?.nextPath });

  const submitData = (type) => {
    if (JointTenantsAccountType[AccountData?.AccountType] === type) {
      onSuccess();
      return;
    }

    if (!AccountData?.AccountType) {
      dispatch(
        createAccountType({
          data: {
            JointTenantsAccountType: type,
          },
          ApplicationUid,
          onSuccess,
        }),
      );
      return;
    }

    dispatch(
      patchAccountType({
        data: {
          JointTenantsAccountType: type,
        },
        ApplicationUid,
        onSuccess,
      }),
    );
  };

  const options = [
    {
      title: t('fundAccount.survivingHolderTitle'),
      postTitle: t('fundAccount.survivingHolderSubtitle'),
      description: t('fundAccount.survivingHolderDescription'),
      onClick: () => {
        handleGtmEvent();
        submitData(ACCOUNT_TYPE_JOINT_TENANTS_WITH_RIGHT_OF_SURVIVORSHIP);
      },
      dataCy: 'button-joint-wros',
    },
    {
      title: t('fundAccount.estateTitle'),
      postTitle: t('fundAccount.estateSubtitle'),
      description: t('fundAccount.estateDescription'),
      showDivider: false,
      onClick: () => {
        handleGtmEvent();
        submitData(ACCOUNT_TYPE_JOINT_TENANTS_COMMON);
      },
      dataCy: 'button-joint-tic',
    },
  ];

  const onBackButtonClick = () => {
    const funding = ApplicationFunding || BankApplicationFunding;

    if (funding?.Amount) {
      const fundingType = parseEnumType(
        APPLICATION_FUNDING_TYPE,
        funding?.BankFundingType,
      );

      switch (fundingType) {
        case APPLICATION_FUNDING_TYPE[0]:
          return activeItem.previousPath.PLAID;
        case APPLICATION_FUNDING_TYPE[2]:
          return activeItem.previousPath.WIRE;

        case APPLICATION_FUNDING_TYPE[3]:
          return activeItem.previousPath.CHECK;

        case APPLICATION_FUNDING_TYPE[4]:
          return activeItem.previousPath.CRYPTOCURRENCY;

        default:
          break;
      }
    }

    if (ApplicationFunding?.Amount || BankApplicationFunding?.Amount) {
      return activeItem?.previousPath;
    }

    return activeItem?.preSkipPath;
  };

  return (
    <OnBoardingContainer
      activeMenu={activeMenu}
      activeItem={activeItem}
      previousPath={onBackButtonClick()}
    >
      <Helmet>
        <title>
          {t('fundAccount.titleTag', {
            brokerName: BrokerName,
          })}
        </title>
      </Helmet>
      <Heading mb={28}>{t('fundAccount.survivalTitle')}</Heading>
      {options.map((option) => (
        <FundingContainer {...option} key={option.title} />
      ))}
    </OnBoardingContainer>
  );
};

export default FundJointSurvivor;
